import type { Locale, MeResponse } from '@dbrain/shared';
import { TAB_TITLE } from '../tab-title.js';
import { Alert, Button, Select, useMenus } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { keys, useMe } from '../api/hooks.js';
import { useProductOn } from '../api/license.js';
import { useExperience } from '../experience.js';
import { setLocale } from '../i18n/index.js';
import { useDetectTimeZone } from '../time-zone.js';
import { ErrorAlert } from './ErrorAlert.js';
import { ProductOffPage } from './license/ProductOffPage.js';
import { ShellContext } from './shell/context.js';
import { ProWelcome } from './billing/ProWelcome.js';
import { PlanOfferProvider } from './plan/PlanOffer.js';
import { Sidebar, type SidebarMode } from './shell/Sidebar.js';
import { TopBar } from './shell/TopBar.js';

export function LanguageSwitcher({ onChange }: { onChange?: (locale: Locale) => void }) {
  const { t, i18n } = useTranslation();
  return (
    <label className="lang-switch">
      <span className="ui-visually-hidden">{t('nav.language')}</span>
      <Select
        size="sm"
        value={i18n.language}
        data-testid="language-switcher"
        onChange={(e) => {
          const locale = e.target.value as Locale;
          void setLocale(locale);
          onChange?.(locale);
        }}
      >
        <option value="tr">{t('lang.tr')}</option>
        <option value="en">{t('lang.en')}</option>
      </Select>
    </label>
  );
}

function VerifyBanner() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const resend = useMutation({
    mutationFn: () => api('POST', '/api/auth/resend-verification'),
    onSuccess: () => setSent(true),
  });
  return (
    <div className="banner" data-testid="verify-banner">
      <Alert
        tone="warning"
        title={t('verifyBanner.text')}
        action={
          sent ? (
            <span>{t('verifyBanner.sent')}</span>
          ) : (
            <Button size="sm" variant="secondary" busy={resend.isPending} onClick={() => resend.mutate()}>
              {t('verifyBanner.resend')}
            </Button>
          )
        }
      />
      <ErrorAlert error={resend.error} />
    </div>
  );
}

const SIDEBAR_KEY = 'oppy.sidebar';
/** the admin pages' tables need the room: below this width they start with the rail */
const ADMIN_WIDE = '(min-width: 1440px)';
/** the admin pages' sidebar opened on a narrow window stays open for the browser session */
const ADMIN_SIDEBAR_KEY = 'oppy.sidebar.admin';

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window.matchMedia !== 'function' || window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * The user's own choice wins; without one a page with a right-hand panel gets the rail, as in the mockups.
 * The admin pages on a window narrower than 1440 px start with the rail whatever the choice; opened there,
 * the sidebar stays open on them for the session, and the choice for the other pages does not change.
 */
function useSidebarMode(panel: boolean, admin: boolean): [SidebarMode, (mode: SidebarMode) => void] {
  const wide = useMedia(ADMIN_WIDE);
  const [stored, setStored] = useState<SidebarMode | null>(() => {
    try {
      const value = localStorage.getItem(SIDEBAR_KEY);
      return value === 'rail' || value === 'expanded' ? value : null;
    } catch {
      return null;
    }
  });
  const [adminOpen, setAdminOpen] = useState(() => {
    try {
      return sessionStorage.getItem(ADMIN_SIDEBAR_KEY) === 'expanded';
    } catch {
      return false;
    }
  });
  const narrowAdmin = admin && !wide;
  const mode = narrowAdmin ? (adminOpen ? 'expanded' : 'rail') : (stored ?? (panel ? 'rail' : 'expanded'));
  const set = (next: SidebarMode) => {
    if (narrowAdmin) {
      setAdminOpen(next === 'expanded');
      try {
        sessionStorage.setItem(ADMIN_SIDEBAR_KEY, next);
      } catch {
        // private mode: the choice lasts for this page only
      }
      return;
    }
    setStored(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next);
    } catch {
      // private mode: the choice lasts for this page only
    }
  };
  return [mode, set];
}

/** The phone's menu: the whole left menu over the page; Escape or the backdrop closes it. */
function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.querySelector<HTMLElement>('[data-testid="sidebar-toggle"]')?.focus();
    document.body.classList.add('ui-scroll-lock');
    return () => {
      document.body.classList.remove('ui-scroll-lock');
      previous?.focus();
    };
  }, []);
  return (
    <div
      ref={ref}
      className="drawer"
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.mainNav')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      data-testid="drawer"
    >
      <div className="drawer__backdrop" onClick={onClose} aria-hidden="true" />
      {children}
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  useMenus();
  const me = useMe();
  useDetectTimeZone(Boolean(me.data));
  const experience = useExperience();
  const productOn = useProductOn(experience);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [panel, setPanel] = useState(false);
  const [mode, setMode] = useSidebarMode(panel, location.pathname.startsWith('/admin'));
  const [drawer, setDrawer] = useState(false);
  // one value for the page's lifetime: pages depend on it in effects
  const shell = useMemo(() => ({ experience, openDrawer: () => setDrawer(true), setPanel }), [experience]);
  const logout = useMutation({
    mutationFn: () => api('POST', '/api/auth/logout'),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(keys.me, null);
      void navigate('/login');
    },
  });
  // the tab says Oppy in both products; a conversation adds the approvals waiting in it ("(2) Oppy")
  useEffect(() => {
    if (!location.pathname.startsWith('/c/')) document.title = TAB_TITLE;
  }, [location.pathname]);
  // a page change closes the phone menu
  useEffect(() => setDrawer(false), [location.pathname]);
  const user: MeResponse | null | undefined = me.data;
  if (!user) return <>{children}</>;
  // D-125: an address whose product the license does not have shows only that
  if (!productOn) return <ProductOffPage experience={experience} onLogout={() => logout.mutate()} loggingOut={logout.isPending} />;
  // a conversation draws its own bar (its title and controls)
  const ownBar = location.pathname.startsWith('/c/');
  const sidebar = (props: { drawer?: boolean }) => (
    <Sidebar
      me={user}
      experience={experience}
      mode={mode}
      drawer={props.drawer}
      onToggle={props.drawer ? () => setDrawer(false) : () => setMode(mode === 'rail' ? 'expanded' : 'rail')}
      onNavigate={props.drawer ? () => setDrawer(false) : undefined}
      onLogout={() => logout.mutate()}
      loggingOut={logout.isPending}
    />
  );
  return (
    <ShellContext.Provider value={shell}>
      <PlanOfferProvider>
      <a href="#main" className="skip-link">
        {t('shell.skip')}
      </a>
      {/* a conversation fills the window: its turns scroll inside it, the bar and the composer stay (D-79) */}
      <div className={`shell shell--${mode}${ownBar ? ' shell--fill' : ''}`} data-experience={experience}>
        {sidebar({})}
        {drawer ? <Drawer onClose={() => setDrawer(false)}>{sidebar({ drawer: true })}</Drawer> : null}
        <div className="shell__main">
          {ownBar ? null : <TopBar experience={experience} />}
          {!user.user.emailVerified ? <VerifyBanner /> : null}
          <main id="main" className="shell__content" tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>
      {/* Phase 16c (B2cOdemeTamam): the checkout's payment was taken */}
      <ProWelcome />
      </PlanOfferProvider>
    </ShellContext.Provider>
  );
}

export function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  useMenus();
  return (
    <div className="auth-page">
      <header className="auth-top">
        <span className="auth-brand">
          <span className="auth-brand__mark" aria-hidden="true">
            O
          </span>
          {t('common.appName')}
        </span>
        <LanguageSwitcher />
      </header>
      <main id="main" className="auth-card">
        <h1>{title}</h1>
        {children}
      </main>
      <p className="auth-foot">{t('auth.footer')}</p>
    </div>
  );
}
